<template>
  <main class="admin-requests">
    <div class="admin-requests__wrapper">
      <div class="admin-requests__controls">
        <div class="admin-requests__filters">
          <h1 class="admin-requests__page-title">заявки</h1>
          <filter-selector
            class="admin-requests__filter-selector"
            v-model="selectedFilter"
            :options="filterOptions"
            :name="filterName"
          ></filter-selector>
        </div>
        <div class="admin-requests__search">
          <input
            @keyup.prevent.enter="performSearch"
            @input="clearSearch"
            class="admin-requests__search-field"
            v-model="searchRequest"
            type="search"
            placeholder="Поиск"
          >
        </div>
      </div>
      <div class="admin-requests__table">
        <requests-table
          @reject-request="initiateRejection"
          :requests="requests"
          :showActions="showActions"
        ></requests-table>
        <div ref="observable"></div>
      </div>
    </div>
    <reject-popup
      :isShown="showPopup"
      @cancel-rejection="showPopup = false"
      @reject="rejectRequest"
    ></reject-popup>
  </main>
</template>

<script>
import FilterSelector from '../components/FilterSelector'
import RequestsTable from '../components/RequestsTable'
import RejectPopup from '../components/RejectPopup'
import algoliasearch from 'algoliasearch/lite'
import { apiGetRequests, apiRejectRequest } from '../utils/api'
import { CHANGE_NEW_REQUESTS, CHANGE_REQUESTS_FILTER, SET_ALGOLIA_CLIENT } from '../store/mutations-names'

export default {
  name: 'AdminRequests',
  components: {
    FilterSelector,
    RequestsTable,
    RejectPopup
  },
  data() {
    return {
      filterOptions: [
        { name: 'по дате', value: 'creationDate'},
        { name: 'по возрасту', value: 'age'},
        { name: 'архив', value: 'isArchived'},
      ],
      filterName: 'filters',
      selectedFilter: this.$store.state.adminRequestsFilter,
      searchRequest: '',
      searchFilter: 'name',
      requests: [],
      showPopup: false,
      intersectionObserver: null,
      isLoading: false,
      tmpRejectId: '',
      algoliaIndex: null,
      nextPage: 1
    }
  },
  computed: {
    showActions() {
      return !(this.selectedFilter == 'isArchived')
    }
  },
  watch: {
    selectedFilter() {
      this.$store.commit(CHANGE_REQUESTS_FILTER, this.selectedFilter)
      this.loadInitialData()
    },
    requests() {
      const newRequests = this.requests.reduce((acc, request) => {
        if (request.notSeen) {
          return acc + 1
        } else {
          return acc
        }
      }, 0)
      this.$store.commit(CHANGE_NEW_REQUESTS, newRequests)
    }
  },
  mounted() {
    // init aloglia client if not present in vuex
    if (!this.$store.state.adminAlgoliaClient) {
      const client = algoliasearch('C5SVSHCML6', '41121316da18dbc2067de6a69107a5b8')
      this.$store.commit(SET_ALGOLIA_CLIENT, client)
      this.algoliaIndex = client.initIndex('requests')
    } else {
      this.algoliaIndex = this.$store.state.adminAlgoliaClient.initIndex('requests')
    }
    // setup intersectionObserver
    this.intersectionObserver = new IntersectionObserver(this.loadMoreData, {
      root: null,
      threshold: 0
    })
    this.intersectionObserver.observe(this.$refs.observable)
    // loadInitialData
    this.loadInitialData()
  },
  methods: {
    clearSearch() {
      if (this.searchRequest.length == 0) {
        this.loadInitialData()
      }
    },
    performSearch() {
      // just load data from firestore
      if (this.searchRequest.length == 0) {
        this.loadInitialData()
      } else {
        // perform search and load result
        let filters
        this.selectedFilter == 'isArchived' ? filters = 'isArchived: true' : filters = 'isArchived: false'
        this.algoliaIndex.search(this.searchRequest, { filters })
          .then(({ hits }) => {
            if (this.selectedFilter == 'creationDate') {
              hits.sort((a, b) => b.creationDate - a.creationDate)
            } else if (this.selectedFilter == 'age') {
              hits.sort((a, b) => a.age - b.age)
            }
            this.requests = hits
          })
          .catch((error) => {
            console.error(error)
          })
      }
    },
    async loadInitialData() {
      this.requests = []
      this.nextPage = 1
      this.isLoading = true
      try {
        let result = await apiGetRequests(this.nextPage, this.selectedFilter)
        this.requests = result.docs
        if (result.hasNextPage) {
          this.nextPage++
        } else {
          this.nextPage = null
        }
      } catch(e) {
        console.error(e.message)
        this.isLoading = false
      }
      this.isLoading = false
    },
    async loadMoreData(entries) {
      if (this.isLoading || !this.nextPage) {
        return
      }
      this.isLoading = true
      entries.forEach(async (entry) => {
        if (entry.isIntersecting) {
          try {
            let result = await apiGetRequests(this.nextPage, this.selectedFilter)
            this.requests.push(...result.docs)
            if (result.hasNextPage) {
              this.nextPage++
            } else {
              this.nextPage = null
            }
          } catch(e) {
            console.error(e.message)
            this.isLoading = false
          }
          this.isLoading = false
        }
      })
      this.isLoading = false
    },
    async rejectRequest() {
      try {
        const result = await apiRejectRequest(this.tmpRejectId)
        if (result) {
          const index = this.requests.findIndex((el) => el.id == this.tmpRejectId)
          if (index >= 0) {
            this.requests.splice(index, 1)
            this.tmpRejectId = ''
          }
        }
      } catch (e) {
        console.error(e.message)
      }
      this.showPopup = false
    },
    initiateRejection(id) {
      this.showPopup = true
      this.tmpRejectId = id
    }
  }
}
</script>

<style lang="scss" scoped>
@use "../styles/fonts";
@use "../styles/colors";

.admin-requests {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 60px 45px 20px;
  &__wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    max-width: 1328px;
  }
  &__controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
  }
  &__page-title {
    display: inline-block;
    margin-right: 35px;
    font-size: 22px;
    text-transform: uppercase;
  }
  &__search {
    display: flex;
    align-items: center;
  }
  &__search-field {
    width: 290px;
    height: 40px;
    padding: 0 12px;
    margin-left: 15px;
    border: 1px solid #000;
    border-radius: 0;
    font-size: 16px;
    &::placeholder {
      color: colors.$grayb2;
    }
  }
  &__table {
    flex-grow: 1;
    overflow-x: scroll;
  }
}

@media screen and (max-width: 1023px) {
  .admin-requests {
    padding: 50px 12px 20px;
  }
}

@media screen and (max-width: 860px) {
  .admin-requests {
    &__controls {
      display: block;
    }
    &__filters {
      margin-bottom: 15px;
    }
    &__page-title {
      margin-right: 20px;
    }
    &__search-field {
      flex-grow: 1;
      width: auto;
      margin-left: 0px;
    }
  }
}

@media screen and (max-width: 860px) {
  .admin-requests {
    &__filter-selector {
      margin-top: 10px;
    }
  }
}
</style>